import React from 'react';
import PropTypes from 'prop-types';

import { sortByWeight } from '@techchange/dnd';
import { UserAvatarList } from '@techchange/filestack';
import { formShape, FIELD_TYPES } from '@techchange/components';
import { ProgressBar, appPropTypes, useLanguageContext } from '@techchange/components';

import './PollResults.scss';

const propTypes = {

	/**
	 * The poll form data.
	 */
	form: formShape.isRequired,

	/**
	 * The list of form entries for the poll.
	 */
	formEntries: PropTypes.arrayOf(PropTypes.shape({

		id: PropTypes.string,

	})),

	/**
	 * The current user's ID.
	 */
	userId: PropTypes.string,

	/**
	 * The users related to the scope of this content,
	 * (i.e. all enrolled users in a session).
	 *
	 * NOTE: Map from user ID -> user data.
	 */
	users: appPropTypes.allUsers.isRequired,

	/**
	 * Callback function to handle clicking on a user.
	 */
	onUserClick: PropTypes.func.isRequired,

};

/**
 * Calculates the margin required to place inline elements next to a filled in progress bar.
 *
 * @param   {number} pct     The percentage of the progress bar that's filled in.
 * @param   {number} width   The width of the progress bar (px).
 * @returns {number} margin  The required margin.
 */
const calculateMargin = (pct, width) => {

	// Calculate the percentage of the progress bar that's filled in
	const pctFilled = pct / 100;

	// Calculate the width of the progress bar that's filled in
	const widthFilled = width * pctFilled;

	// Position the next element to the right of the progress bar that's filled in
	const margin = widthFilled;

	return margin;

};

const PROGRESS_BAR_WIDTH_PX = 300; // The width of the progress bars in pixels
const PROGRESS_BAR_WIDTH_PCT = 90; // The width of the progress bars in percentage

const PollResults = (props) => {

	const {
		form,
		formEntries,
		userId,
		users,
		onUserClick,
	} = props;

	const { chooseContentFromLanguage } = useLanguageContext();

	// Calculate the results of the poll and aggregate by field and entries
	const fields = aggregateResults(form, formEntries, users, chooseContentFromLanguage);

	return (
		<div className="poll-results-container">
			{fields.map((field) => {

				// Get the translated content for the form field
				const formFieldContent = chooseContentFromLanguage(field.form_field_contents);

				return (
					<div
						key={field.id}
						className="poll-results-field">
						<legend
							role="heading"
							aria-level="6"
							className="col-form-legend tc-form-question-text">
							{formFieldContent?.title || field.title}
						</legend>

						{/* Display results in row and col format on xs, sm, md devices */}
						<ul className="d-lg-none">
							{field.form_field_options?.map((option) => (
								<li
									key={option.id}
									className="poll-results-option-container">
									<p className="poll-results-option-label">
										{option.label}
									</p>
									<div className="poll-results-aggregated">
										<span
											className="poll-results-progress"
											style={{ width: `${PROGRESS_BAR_WIDTH_PCT}%` }}
											title={`${option.result.users.length > 0 ? option.result.pct : 0}%`}>
											<ProgressBar progress={option.result.pct} />
										</span>
										<span
											className="poll-results-num-users"
											style={{
												// Place the num responses to the right of filled portion of progress bar
												position: 'absolute',
												left: `${calculateMargin(option.result.pct, PROGRESS_BAR_WIDTH_PCT)}%`,
												marginLeft: 10,
											}}>
											{`${option.result.users.length}`}
										</span>
									</div>
									{option.result.users.length > 0 &&
										<UserAvatarList
											users={option.result.users}
											userId={userId}
											numToShow={5}
											stacked
											onClickUser={onUserClick} />
									}
								</li>
							))}
						</ul>

						{/* Display results in table format on lg, xl devices */}
						<div className="d-none d-lg-block">
							<table className="poll-results-table">
								<tbody>
									{field.form_field_options?.map((option) => (
										<tr key={option.id}>
											<td>
												<span>{option.label}</span>
											</td>
											<td className="poll-results-table-col-aggregated">
												<div className="poll-results-aggregated">
													<span
														className="poll-results-progress"
														style={{ width: PROGRESS_BAR_WIDTH_PX }}
														title={`${option.result.users.length > 0 ? option.result.pct : 0}%`}>
														<ProgressBar progress={option.result.pct} />
													</span>
													<span
														className="poll-results-num-users"
														style={{
															// Place num responses to the right of filled portion of progress bar
															// eslint-disable-next-line max-len
															marginLeft: calculateMargin(option.result.pct, PROGRESS_BAR_WIDTH_PX) - PROGRESS_BAR_WIDTH_PX + 8,
														}}>
														{`${option.result.users.length}`}
													</span>
													<UserAvatarList
														users={option.result.users}
														userId={userId}
														numToShow={5}
														stacked
														onClickUser={onUserClick} />
												</div>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

					</div>
				);

			})}
		</div>
	);

};

PollResults.propTypes = propTypes;

export default PollResults;

/**
 * Aggregates the results of the poll by field and form entries.
 *
 * @param {object} form        The form.
 * @param {array} formEntries  The form entries.
 * @param {object} users       The list of enrolled users in the session.
 * @param {func} chooseContentFromLanguage  Callback function to choose translated content.
 *
 * @returns {array} fields  The updated form fields with aggregated results.
 */
function aggregateResults(form, formEntries, users, chooseContentFromLanguage) {

	// Aggregate the form entry results for each form field and option
	const results = {};

	if (formEntries && users) {

		// Loop through each form entry
		formEntries.forEach((entry) => {

			const formValues = entry.form_values || [];

			// Loop through each form value
			formValues.forEach((value) => {

				const fieldId = value.form_field_id;
				const userId = value.user_id;
				const user = users[userId];

				if (user) {

					// Get the form field option that was selected by the user
					const selectedOptionID = value.selected_form_field_options?.[0]?.id;

					// Create the results for the form field (if needed)
					if (!results[fieldId]) {
						results[fieldId] = {};
					}

					// Create the result for the form field and value (if needed)
					if (!results[fieldId][selectedOptionID]) {
						results[fieldId][selectedOptionID] = [];
					}

					// Add the user to the results for the form field and value
					results[fieldId][selectedOptionID].push(user);

				}

			});

		});

	}

	// Sort the fields by weight
	let fields = sortByWeight(form.form_fields);

	// Loop through and update each form field to have the aggregated poll results
	fields = fields.map((formField) => {

		// Create a copy of the field and options
		const field = { ...formField };

		// Get the results for this form field
		const responses = results[field.id] || {};

		// Calculate the total number of responses for this field
		const totalResponses = Object.values(responses).reduce(
			(total, userIds) => total + userIds.length, 0);

		// Sort the options by weight
		field.form_field_options = sortByWeight(field.form_field_options);

		// Loop through and add the poll results to each option
		field.form_field_options = field.form_field_options?.map((option) => {

			// Get the list of users who responded with this option
			const resultUsers = responses[option.id] || [];

			// Calculate the percentage of responses for this option
			let pct = 100.0 * (resultUsers.length / totalResponses);

			// Show at least a little something for options with no responses (half a pct)
			if (pct === 0) {
				pct = 0.5;
			}

			return {
				id: option.id,
				label: renderOptionLabel(
					field.type,
					option,
					field.form_field_options,
					chooseContentFromLanguage,
				),
				value: option.value,
				result: {
					users: resultUsers,
					pct: parseFloat(pct.toFixed(1)),
				},
			};

		});

		return field;

	});

	return fields;

}

/**
 * Renders the label text for the option.
 *
 * @param {string} type    The form field type.
 * @param {object} option  The option.
 * @param {array} options  The list of options.
 * @param {func} chooseContentFromLanguage  Callback function to choose translated content.
 *
 * @returns {string} The user friendly label text for the option.
 */
function renderOptionLabel(type, option, options, chooseContentFromLanguage) {

	// Get the translated content for the form field option
	const optionContent = chooseContentFromLanguage(option.form_field_option_contents);

	const optionLabel = optionContent?.label || option.label;

	// Check if the form field is a likert scale question
	if (type === FIELD_TYPES.LIKERT) {

		const likertValue = option.value.toString();

		if (optionLabel !== likertValue) {

			// Count the number of options that have custom label text
			const numWithLabels = options.filter((tempOption) => {

				// Get the translated content for the form field option
				const tempOptionContent = chooseContentFromLanguage(tempOption.form_field_option_contents);

				const tempLikertValue = tempOption.value.toString();
				const tempLikertLabel = tempOptionContent?.label || tempOption.label;

				return tempLikertLabel !== tempLikertValue;

			}).length;

			// Check if all the options have a custom label
			if (numWithLabels === options.length) {
				return optionLabel;
			}

			// Otherwise, combine the option value and the custom label
			return `${option.value} - ${optionLabel}`;

		}

		// Otherwise, just use the option value
		return option.value;

	}

	// Otherwise, just use the option label (if available)
	if (optionLabel) {
		return optionLabel;
	}

	// Otherwise, just use the option value
	return option.value;

}
